import React, { useRef, useCallback } from 'react';
import { useDrop, useDrag, DragElementWrapper, DragSourceOptions, DragObjectWithType } from 'react-dnd';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import TextTruncate from 'react-text-truncate';

import * as GoalsSelectors from 'Shared/selectors/Goals.selectors';
import { useToggle } from 'Shared/utils/hooks';

import * as UsersSelectors from 'Shared/selectors/Users.selectors';
import { getWorkspaceByUuid } from 'Shared/selectors/workspace';

import Colors from 'Wpds/colors/ColorConstants';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpProgressBar from 'Wpds/components/progress/WpProgressBar';
import { GoalItem } from 'Shared/constants/types';
import { dateMMDDYY, getIsDateAfter } from 'Shared/utils/DateTime.utils';

import GoalIcon from '../GoalIcon';
import GoalCardDropdown from '../GoalCardDropdown/GoalCardDropdown';
import GoalCardDropArea from '../GoalCardDropArea/GoalCardDropArea';
import CompleteGoalModal from '../CompleteGoalModal/CompleteGoalModal';
import { dropSpec, dragSpec } from '../dndSpecs';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';

import './GoalCard.scss';

type Props = {
  goal: GoalItem;
  classnames?: string;
  moveCard?: Function;
  index?: number;
  onDrop?: Function;
  draggable?: boolean;
  onMarkComplete?: Function;
  handleMarkIncomplete?: Function;
  handleChangeStatus?: Function;
  redirectToGoalDetailPage?: Function;
};

export function getLinkTooltip(parentGoal: GoalItem, childGoals: Array<GoalItem>) {
  if (parentGoal) return `Child of: ${parentGoal.name}`;
  if (childGoals.length) {
    const extraGoals = childGoals.length - 3;
    return `Parent of: ${childGoals
      .slice(0, 3)
      .map(e => e.name)
      .join(', ')} ${extraGoals > 0 ? `, + ${extraGoals} more` : ''}`;
  }
  return null;
}

function getStatusLabel(status) {
  switch (status) {
    case 'on_track':
      return 'On track';
    case 'at_risk':
      return 'At risk';
    case 'missed':
      return 'Missed';
    case 'achieved':
      return 'Achieved';
    default:
      return null;
  }
}

function getOtherUserId(goal: GoalItem, userId: number) {
  if (goal.privacySetting === 'meAndATeammate' || goal.privacySetting === 'meAndChoosenTeammate') {
    const possibleUserIds = [];
    possibleUserIds.push(goal.viewerId);
    possibleUserIds.push(goal.assignerId);
    possibleUserIds.push(goal.assigneeId);
    return possibleUserIds.find(id => id !== userId);
  } else return null;
}

function getPrivacyTooltip({ goal, otherUser, goalWorkspaceName }) {
  if (goal.privacySetting === 'groupWorkspace') {
    return `Anyone in "${goalWorkspaceName}" can see this goal`;
  }

  let privacyLabel = 'This goal can only be seen by: You';
  if (otherUser && (goal.privacySetting === 'meAndATeammate' || goal.privacySetting === 'meAndChoosenTeammate')) {
    privacyLabel += ` and ${otherUser.firstName}`;
  }
  return privacyLabel;
}

const GoalCard = ({
  goal,
  classnames,
  moveCard,
  index,
  onDrop,
  draggable = false,
  onMarkComplete,
  handleMarkIncomplete,
  handleChangeStatus,
  redirectToGoalDetailPage,
}: Props) => {
  const [сompleteGoalModalIsOpen, completeGoalModalIsOpenToggle] = useToggle(false);
  const goalCardRef = useRef(null);
  const privacyIconRef = useRef(null);
  const linkIconRef = useRef(null);
  const { id: userId } = useCurrentUser();
  const goalWorkspaceName = useSelector(state => getWorkspaceByUuid(goal.workspaceSlug)(state)?.name);
  const { slug, resolutionNote } = goal;
  const statusLabel = getStatusLabel(goal.status);
  const otherUserId = getOtherUserId(goal, userId);
  const otherUser = useSelector(state => UsersSelectors.getUserByUserId(state, { userId: otherUserId }))?.toJS();
  const parentGoal = useSelector(state => GoalsSelectors.getGoalById(state, { id: goal.parentId }))?.toJS();
  const childGoals = useSelector(state => GoalsSelectors.getGoalsByParentId(state, { parentId: goal.id }))?.toJS();

  const linkTooltip = getLinkTooltip(parentGoal, Object.values(childGoals));
  const privacyTooltip = getPrivacyTooltip({ goal, otherUser, goalWorkspaceName });
  const showPrivateIndicator = goal.private;

  const formatDate = dateMMDDYY(goal.endDate);
  const isPastDue = getIsDateAfter(goal.endDate);

  const showDueDate = !goal.complete;
  const canChangeStatus = userId === goal.assigneeId || userId === goal.assignerId;

  const totalNumActionItems = goal.numCompleteActionItems + goal.numIncompleteActionItems;

  const progressBarPercentage =
    totalNumActionItems === 0 ? 0 : (goal.numCompleteActionItems * 100) / totalNumActionItems;

  let actionsItemsLabel =
    totalNumActionItems > 0 ? `${goal.numCompleteActionItems}/${totalNumActionItems} action items` : 'No Action Items';
  actionsItemsLabel = goal.complete ? `Completed on ${dateMMDDYY(goal.completedAt)}` : actionsItemsLabel;

  const [, drop] = useDrop(dropSpec({ ref: goalCardRef, move: moveCard, index, onDrop }));

  const handleMarkComplete = useCallback(() => {
    completeGoalModalIsOpenToggle();
  }, [completeGoalModalIsOpenToggle]);

  const [{ isDragging }, drag] = useDrag<
    DragObjectWithType,
    DragElementWrapper<DragSourceOptions>,
    { isDragging: boolean }
  >(dragSpec({ index }));

  const classes = classNames('GoalCard', classnames, `GoalCard--${goal.status}`, {
    'GoalCard--past-due': isPastDue,
    'GoalCard--is-dragging': isDragging,
    'GoalCard--draggable': draggable,
  });

  drag(drop(draggable ? goalCardRef : null));

  const handleCardClick = useCallback(() => {
    redirectToGoalDetailPage(slug);
  }, [redirectToGoalDetailPage, slug]);

  return (
    <>
      <div className={classes} ref={goalCardRef} onClick={handleCardClick}>
        {isDragging ? (
          <GoalCardDropArea />
        ) : (
          <>
            <div className="GoalCard__header">
              <div className="GoalCard__header__icon">
                <GoalIcon icon={goal.icon} status={goal.status} size="lg" />
              </div>
              <div className="GoalCard__header__title">
                <TextTruncate line={2} truncateText="…" text={goal.name} />
                {statusLabel && <span className="GoalCard__header__title__status">{statusLabel}</span>}
              </div>
              <div className="d-flex">
                {linkTooltip ? (
                  <WpTooltip placement="top">
                    <WpTooltipTrigger>
                      <span className="p-1 mr-1" ref={linkIconRef}>
                        <WpIcon name="link" color={Colors.gray[800]} />
                      </span>
                    </WpTooltipTrigger>
                    <WpTooltipContent>{linkTooltip}</WpTooltipContent>
                  </WpTooltip>
                ) : null}
                {showPrivateIndicator && (
                  <WpTooltip placement="top">
                    <WpTooltipTrigger>
                      <span className="p-1" ref={privacyIconRef}>
                        <WpIcon name="user-lock" />
                      </span>
                    </WpTooltipTrigger>
                    <WpTooltipContent>{privacyTooltip}</WpTooltipContent>
                  </WpTooltip>
                )}
                {canChangeStatus && (
                  <GoalCardDropdown
                    slug={slug}
                    goal={goal}
                    handleMarkComplete={handleMarkComplete}
                    handleMarkIncomplete={handleMarkIncomplete}
                    handleChangeStatus={handleChangeStatus}
                    redirectToGoalDetailPage={redirectToGoalDetailPage}
                  />
                )}
              </div>
            </div>
            <div className="GoalCard__stats">
              <WpProgressBar percent={progressBarPercentage} />
              <div className="GoalCard__stats__details">
                <div className="GoalCard__stats__details__action-items">{actionsItemsLabel}</div>
                {showDueDate && <div className="GoalCard__stats__details__due">{formatDate}</div>}
              </div>
            </div>
          </>
        )}
      </div>
      {сompleteGoalModalIsOpen ? (
        <CompleteGoalModal
          goalSlug={goal.slug}
          goalResolutionNote={resolutionNote}
          onSubmit={onMarkComplete}
          onToggle={completeGoalModalIsOpenToggle}
        />
      ) : null}
    </>
  );
};

export default GoalCard;
